import { useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { TAB_BAR_MEDIA_QUERY, linkTabPath, tabChangeOf, useOpenTabs, type TabSwitchState } from './openTabs';

/**
 * Hält die Tab-Leiste mit dem Router im Gleichschritt (ADR 0111) — einmal in
 * der App-Hülle aufgerufen:
 *
 * - lädt die Tabs der angemeldeten Person,
 * - zieht bei jeder Adresse den aktiven Tab nach (`syncLocation`),
 * - macht aus ⌘/Strg-Klick und Mittelklick auf interne Links einen ProERP-Tab
 *   im Hintergrund (⌘/Strg+⇧: im Vordergrund). Der Rechtsklick „Link in neuem
 *   Tab öffnen" bleibt dem Browser — wer einen echten Browser-Tab will, nimmt ihn.
 *
 * Unterhalb von `tablet` (keine Leiste) bleibt der Klick beim Browser.
 * Abgefangen wird in der Bubble-Phase am Dokument: Hat eine Komponente den
 * Klick schon selbst behandelt (`preventDefault`), bleibt es dabei.
 */
export function useTabSync(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = useCurrentUser().data?.data.id;
  const hydrate = useOpenTabs((state) => state.hydrate);
  const syncLocation = useOpenTabs((state) => state.syncLocation);
  const applyTabChange = useOpenTabs((state) => state.applyTabChange);
  const path = location.pathname + location.search + location.hash;
  const tabChange = tabChangeOf(location.state);

  // Layout-Effekte: Sie laufen vor den normalen Effekten aller Seiten. So steht der
  // Tab schon auf der neuen Adresse, wenn die Seite ihren Titel meldet (`useTabTitle`
  // in den Brotkrumen) — sonst ginge der Titel an den noch alten Tab verloren.
  useLayoutEffect(() => {
    if (userId) hydrate(userId);
  }, [userId, hydrate]);

  // Erst eine mitgereiste Tab-Änderung (Schließen des aktiven Tabs mit ungespeicherten
  // Änderungen, `deferTabChange`), dann die Adresse — so gilt das Schließen nur, wenn
  // die Seite das Verlassen nicht abgelehnt hat.
  useLayoutEffect(() => {
    if (!userId) return;
    if (tabChange) applyTabChange(tabChange);
    syncLocation(path);
  }, [userId, path, tabChange, syncLocation, applyTabChange]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      const isMiddle = event.type === 'auxclick' && event.button === 1;
      const isModified = event.type === 'click' && event.button === 0 && (event.metaKey || event.ctrlKey) && !event.altKey;
      if ((!isMiddle && !isModified) || event.defaultPrevented) return;
      if (!useOpenTabs.getState().userId || !window.matchMedia(TAB_BAR_MEDIA_QUERY).matches) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const target = linkTabPath(anchor, window.location.origin);
      if (!target) return;
      event.preventDefault();
      if (isModified && event.shiftKey) {
        const tabId = useOpenTabs.getState().prepareTab(target);
        navigate(target, { state: { tabSwitch: tabId } satisfies TabSwitchState });
        return;
      }
      useOpenTabs.getState().openInBackground(target);
    }
    document.addEventListener('click', onClick);
    document.addEventListener('auxclick', onClick);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('auxclick', onClick);
    };
  }, [navigate]);
}

/**
 * Hauptüberschrift der Seite als Ersatz-Titel des Tabs: Meldet eine Seite
 * keinen Titel (Brotkrumen, `useTabTitle`), steht im Tab ihr `<h1>` — auf
 * Detailseiten also der Datensatz. Beobachtet den Inhaltsbereich, weil die
 * Überschrift oft erst mit den Daten kommt.
 */
export function useTabHeading(containerRef: React.RefObject<HTMLElement>): void {
  const { pathname } = useLocation();
  const setLabel = useOpenTabs((state) => state.setLabel);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      const text = container.querySelector('h1')?.textContent?.replace(/\s+/g, ' ').trim().slice(0, 80);
      if (text) setLabel(pathname, 'heading', text);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    schedule();
    const observer = new MutationObserver(schedule);
    observer.observe(container, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [containerRef, pathname, setLabel]);
}
